import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { Save, X, User, Check, AlertCircle } from 'lucide-react';

interface FormData {
  name: string;
  isPremium: boolean;
  isReviewModeCharacter: boolean;
}

const CharacterForm: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<FormData>({
    name: '',
    isPremium: false,
    isReviewModeCharacter: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      setError('Karakter adı boş olamaz');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await api.post('/character/create', {
          name: formData.name,
          isPremium: formData.isPremium,
          isReviewModeCharacter: formData.isReviewModeCharacter,
      });

      setSuccess('Karakter başarıyla oluşturuldu');
      setTimeout(() => {
        navigate('/');
      }, 1500);
      
    } catch (err) {
      setError('Karakter oluşturulurken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-sm overflow-hidden">
      <div className="bg-gradient-to-r from-emerald-600 to-teal-700 py-6 px-8">
        <div className="flex justify-between items-center">
          <h2 className="text-2xl font-bold text-white flex items-center">
            <div className="mr-3 bg-white bg-opacity-20 p-2 rounded-full">
              <User className="text-white" size={20} />
            </div>
            Yeni Karakter Oluştur
          </h2>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="p-8">
        {/* Alerts */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center">
            <AlertCircle className="mr-2" size={16} />
            <span>{error}</span>
          </div>
        )}
        
        {success && (
          <div className="mb-6 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center">
            <Check className="mr-2" size={16} />
            <span>{success}</span>
          </div>
        )}

        <div className="space-y-8">
          {/* Basic Info */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-700 border-b pb-2">Karakter Bilgileri</h3>
            
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">Karakter Adı</label>
              <input
                type="text"
                id="name"
                name="name"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition"
                value={formData.name}
                onChange={handleChange}
                placeholder="Karakter adını girin"
              />
              <p className="mt-1 text-xs text-gray-500">
                Karakterin kullanıcılara görüntülenecek adı
              </p>
            </div>

            <div className="flex space-x-6 mt-4">
              <label className="flex items-center space-x-2 cursor-pointer">
                <div className={`w-5 h-5 rounded flex items-center justify-center ${formData.isPremium ? 'bg-emerald-500' : 'border border-gray-300'}`}>
                  {formData.isPremium && <Check className="text-white" size={12} />}
                </div>
                <input
                  type="checkbox"
                  className="hidden"
                  checked={formData.isPremium}
                  onChange={handleChange}
                  name="isPremium"
                />
                <span className="text-sm text-gray-700">Premium Karakter</span>
              </label>

              <label className="flex items-center space-x-2 cursor-pointer">
                <div className={`w-5 h-5 rounded flex items-center justify-center ${formData.isReviewModeCharacter ? 'bg-emerald-500' : 'border border-gray-300'}`}>
                  {formData.isReviewModeCharacter && <Check className="text-white" size={12} />}
                </div>
                <input
                  type="checkbox"
                  className="hidden"
                  checked={formData.isReviewModeCharacter}
                  onChange={handleChange}
                  name="isReviewModeCharacter"
                />
                <span className="text-sm text-gray-700">İnceleme Modu</span>
              </label>
            </div>
          </div>

          <div className="text-sm text-gray-500 p-4 bg-gray-50 rounded-lg">
            <p className="font-medium text-gray-600 mb-1">Bilgi</p>
            <p>Karakter oluşturduktan sonra düzenleme sayfasından görsel ekleyebilirsiniz.</p>
          </div>
        </div>

        <div className="mt-8 pt-5 border-t border-gray-200 flex justify-between">
          <button
            type="button"
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 flex items-center hover:bg-gray-100"
            onClick={() => navigate('/')}
          >
            <X size={16} className="mr-1.5" />
            İptal
          </button>
          
          <button
            type="submit"
            className="px-4 py-2 bg-emerald-600 rounded-md text-sm font-medium text-white flex items-center hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={loading}
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-1.5"></div>
                Oluşturuluyor...
              </>
            ) : (
              <>
                <Save size={16} className="mr-1.5" />
                Karakteri Oluştur
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CharacterForm; 